import React from 'react';
import { ArrowRight, List, DollarSign } from 'lucide-react';
import { parseEDI } from '../services/ediService';

const TX_NAMES = {
    '850': 'Purchase Order',
    '810': 'Invoice',
    '856': 'Ship Notice',
    '855': 'PO Acknowledgment',
    '997': 'Functional Ack',
    '999': 'Impl Acknowledgment',
    '940': 'Warehouse Order',
    '945': 'Warehouse Confirm',
    '820': 'Payment Order',
    '846': 'Inventory Inquiry',
    '852': 'Product Activity',
    '860': 'PO Change',
    '204': 'Motor Carrier Load',
    '214': 'Shipment Status',
    '210': 'Freight Invoice',
    '211': 'Bill of Lading',
    '834': 'Benefit Enrollment',
    '835': 'Payment Remittance',
    '837': 'Healthcare Claim',
};

function resolveSegments(data) {
    if (!data) return [];
    if (typeof data === 'string') {
        const parsed = parseEDI(data);
        return parsed.success ? parsed.segments : [];
    }
    if (data._raw_content) {
        const parsed = parseEDI(data._raw_content);
        return parsed.success ? parsed.segments : [];
    }
    const segs = data.segments;
    if (!segs?.length) return [];
    // Backend format: elements are raw strings — normalize to {position, value} objects
    if (typeof segs[0]?.elements?.[0] === 'string') {
        return segs.map(seg => ({
            ...seg,
            elements: (seg.elements || []).map((val, idx) => ({
                position: idx + 1,
                value: val,
            })),
        }));
    }
    // Already parsed (ediService format): elements are {position, value} objects
    return segs;
}

function extractSummary(data) {
    const segments = resolveSegments(data);
    if (!segments?.length) return null;

    const find = (id) => segments.find(s => s.segment_id === id);
    const elem = (seg, pos) => seg?.elements?.find(e => e.position === pos)?.value?.trim() || null;

    const isa = find('ISA');
    const st = find('ST');
    const beg = find('BEG');
    const big = find('BIG');
    const bsn = find('BSN');
    const bak = find('BAK');
    const bht = find('BHT');
    const b3 = find('B3');
    const w05 = find('W05');
    const xq = find('XQ');
    const b1 = find('B1');
    const b2 = find('B2');
    const ctt = find('CTT');
    const amt = find('AMT');
    const tds = find('TDS');

    const st01 = elem(st, 1);
    const txName = TX_NAMES[st01] || null;

    const isa05 = elem(isa, 5);
    const isa06 = elem(isa, 6);
    const isa07 = elem(isa, 7);
    const isa08 = elem(isa, 8);
    const isa15 = elem(isa, 15);

    const sender = isa05 === 'ZZ' ? isa06 : null;
    const receiver = isa07 === 'ZZ' ? isa08 : null;

    let docNumber = null;
    if (st01 === '850') docNumber = elem(beg, 3);
    else if (st01 === '810') docNumber = elem(big, 2);
    else if (st01 === '856') docNumber = elem(bsn, 2);
    else if (st01 === '855') docNumber = elem(bak, 3);
    else if (st01 === '837') docNumber = elem(bht, 3);
    else if (st01 === '210' || st01 === '214') docNumber = elem(b3, 2);
    else if (st01 === '940' || st01 === '945') docNumber = elem(w05, 2);
    else if (st01 === '852') docNumber = elem(xq, 2);
    else if (st01 === '204') docNumber = elem(b2, 4) || elem(b2, 2);
    else if (st01 === '211') docNumber = elem(b1, 2);

    let lineItems = 0;
    if (st01 === '856') {
        const sn1Count = segments.filter(s => s.segment_id === 'SN1').length;
        if (sn1Count > 0) {
            lineItems = sn1Count;
        } else {
            lineItems = segments.filter(s => s.segment_id === 'HL' && elem(s, 3) === 'I').length;
        }
    } else {
        const lineSegments = ['PO1', 'IT1', 'LX', 'S5'];
        lineItems = segments.filter(s => lineSegments.includes(s.segment_id)).length;
    }
    lineItems = lineItems || null;

    let total = null;
    const tds01 = elem(tds, 1);
    const ctt02 = elem(ctt, 2);
    const amt02 = elem(amt, 2);

    // Invoices (810) usually put standard totals in TDS01 in cents (implied decimal)
    if (st01 === '810' && tds01 && !isNaN(parseFloat(tds01))) {
        total = parseFloat(tds01) / 100;
    } else if (ctt02 && !isNaN(parseFloat(ctt02))) {
        total = parseFloat(ctt02);
    } else if (amt02 && !isNaN(parseFloat(amt02))) {
        total = parseFloat(amt02);
    }

    const env = isa15 === 'P' ? 'Production' : isa15 === 'T' ? 'Test' : null;

    return { txSet: st01, txName, docNumber, sender, receiver, lineItems, total, env };
}

function formatTotal(val) {
    if (val == null) return null;
    return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Keeping a vertically small dot separator for the left group
const Dot = () => <span className="text-[#555555] mx-1.5 opacity-60">·</span>;

export default function EDISummaryHeader({ source, target, isViewMode }) {
    const src = extractSummary(source);
    if (!src) return null;

    const tgt = (!isViewMode && target) ? extractSummary(target) : null;

    const { txSet, txName, docNumber, sender, receiver, lineItems, total, env } = src;

    const lineItemsDelta = tgt && tgt.lineItems != null && src.lineItems != null
        ? tgt.lineItems - src.lineItems
        : null;

    const docLabel = {
        '850': 'PO', '855': 'PO', '860': 'PO',
        '810': 'INV', '856': 'BSN', '820': 'REF',
        '837': 'CLM',
    }[txSet] || 'REF';

    return (
        <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-[#2d2d2d] text-xs w-full bg-[#1e1e1e] flex-wrap">
            {/* Left side: Identity (Type, ID, Routing) */}
            <div className="flex items-center gap-y-1 gap-x-0 flex-wrap shrink min-w-0">
                {txSet && (
                    <span className="text-[#e2e8f0] font-semibold tracking-wide whitespace-nowrap">
                        {txSet}{txName ? ` ${txName}` : ''}
                    </span>
                )}

                {docNumber && (
                    <span className="flex items-center whitespace-nowrap">
                        <Dot />
                        <span className="text-[#808080] font-medium uppercase text-[10px] tracking-wider mr-1.5">{docLabel}</span>
                        <span className="text-[#e2e8f0] font-mono tracking-tight">{docNumber}</span>
                    </span>
                )}

                {(sender || receiver) && (
                    <span className="flex items-center whitespace-nowrap">
                        <Dot />
                        <span className="flex items-center gap-1.5 min-w-0">
                            {sender && <span className="text-[#94a3b8] font-mono truncate">{sender}</span>}
                            {sender && receiver && <ArrowRight size={12} className="text-[#555555] shrink-0" />}
                            {receiver && <span className="text-[#94a3b8] font-mono truncate">{receiver}</span>}
                        </span>
                    </span>
                )}
            </div>

            {/* Right side: Metadata (Count, Total, Environment) */}
            <div className="flex items-center gap-3 shrink-0 flex-wrap">
                {lineItems != null && (
                    <div className="flex items-center gap-1.5 bg-[#252526] px-2 py-0.5 rounded border border-[#333333]">
                        <span className="font-mono text-[#cccccc]">
                            {tgt && tgt.lineItems != null && tgt.lineItems !== lineItems ? (
                                <span className="flex items-center gap-1.5">
                                    <span className="line-through opacity-50">{lineItems}</span>
                                    <ArrowRight size={10} className="text-[#808080] opacity-50" />
                                    <span>{tgt.lineItems} Items</span>
                                    {lineItemsDelta !== 0 && (
                                        <span className={`ml-1 text-[10px] font-bold ${lineItemsDelta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {lineItemsDelta > 0 ? `+${lineItemsDelta}` : lineItemsDelta}
                                        </span>
                                    )}
                                </span>
                            ) : (
                                `${lineItems} Items`
                            )}
                        </span>
                    </div>
                )}

                {total != null && (
                    <div className="flex items-center gap-1 bg-[#252526] px-2 py-0.5 rounded border border-[#333333]">
                        <DollarSign size={12} className="text-[#808080]" />
                        <span className="font-mono text-[#cccccc]">{formatTotal(total)}</span>
                    </div>
                )}

                {env && (
                    <div className="flex items-center">
                        {env === 'Production' ? (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase bg-blue-500/10 text-blue-400 shadow-[inset_0_0_0_1px_rgba(96,165,250,0.2)]">
                                PROD
                            </span>
                        ) : (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase bg-yellow-500/10 text-yellow-400 shadow-[inset_0_0_0_1px_rgba(250,204,21,0.2)]">
                                TEST
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
